// @flow
// $FlowFixMe
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import * as uiActions from '../../actions/ui';
import { getPluralMessage } from '../messages/FilterMessages';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import FacetGroupAllFacetsContent from './FacetGroupAllFacetsContent';
import { getCompoundKey, selectIsBoolean } from '../../selectors/facet';
import FacetGroupAllFacetsModal from '../modals/FacetGroupAllFacetsModal';
import FacetGroupAllFacetsOverlay from '../modals/FacetGroupAllFacetsOverlay';
import { useDesktopMediaQuery } from '../../utils/useMediaQuery';
import * as localListSearchActions from '../../actions/localListSearch';

type OwnProps = {
  location: Object,
  activeFacets: Array<any>,
  filterType: string,
  handleMultipleComposers?: boolean,
  isInApp: boolean,
  id: string | null,
  type: string | null,
};

type MapStateToProps = {
  isBoolean: boolean,
};

type DispatchProps = {
  hideFacetGroupAllFacets: Function,
  setSearchQuery: Function,
};

type Props = OwnProps & MapStateToProps & DispatchProps & { intl: IntlShape };

const FacetGroupAllFacets = ({
  hideFacetGroupAllFacets,
  type,
  id,
  filterType,
  location,
  activeFacets,
  isBoolean,
  intl,
  handleMultipleComposers,
  isInApp,
  setSearchQuery,
}: Props) => {
  const [selectedFacetIds, setSelectedFacetIds] = useState([]);
  const [shouldRenderApplyFiltersBtn, setShouldRenderApplyFiltersBtn] = useState(false);
  const filteredActiveFacets = useMemo(
    () => activeFacets.filter(facet => facet.type === type),
    [activeFacets, type]
  );

  const activeFacetIds = useMemo(
    () =>
      filteredActiveFacets.map(activeFacet =>
        isBoolean ? activeFacet.facet.value : activeFacet.facet.id
      ),
    [filteredActiveFacets, isBoolean]
  );

  useEffect(() => {
    setSelectedFacetIds(activeFacetIds);
    // Only use type in the dep array since the activeFacetIds also change when
    // you apply filters in the overlay window on mobile/capacitor
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickFacet = useCallback(
    (event, newId) => {
      event.preventDefault();
      setSelectedFacetIds(
        selectedFacetIds.includes(newId)
          ? selectedFacetIds.filter(facetId => facetId !== newId)
          : [...selectedFacetIds, newId]
      );
    },
    [selectedFacetIds]
  );

  useEffect(() => {
    if (selectedFacetIds) {
      const isEqual =
        JSON.stringify(selectedFacetIds.sort()) === JSON.stringify(activeFacetIds.sort());
      setShouldRenderApplyFiltersBtn(!isEqual);
    }
  }, [selectedFacetIds, activeFacetIds]);

  const onClose = () => {
    hideFacetGroupAllFacets();

    const compoundKey = getCompoundKey(id, filterType, type);
    setSearchQuery(compoundKey, '');
  };

  const onClickClear = () => {
    setSelectedFacetIds([]);
    onClose();
  };

  const showMobileView = !useDesktopMediaQuery() || isInApp;
  const Wrapper = showMobileView ? FacetGroupAllFacetsOverlay : FacetGroupAllFacetsModal;

  return (
    <Wrapper
      isVisible={!!id}
      hideModal={onClose}
      title={getPluralMessage(intl, type)}
      selectedFacetIds={selectedFacetIds}
      location={location}
      type={type}
      onClickClear={onClickClear}
    >
      {type && id && (
        <FacetGroupAllFacetsContent
          location={location}
          activeFacets={filteredActiveFacets}
          filterType={filterType}
          onClose={onClose}
          type={type}
          id={id}
          isBoolean={isBoolean}
          onClickClear={onClickClear}
          selectedFacetIds={selectedFacetIds}
          onClickFacet={onClickFacet}
          shouldRenderApplyFiltersBtn={shouldRenderApplyFiltersBtn}
          handleMultipleComposers={handleMultipleComposers}
        />
      )}
    </Wrapper>
  );
};

function mapStateToProps(state: Object, ownProps: OwnProps): MapStateToProps {
  return {
    isBoolean: selectIsBoolean(ownProps.type),
  };
}

const dispatchProps: DispatchProps = {
  hideFacetGroupAllFacets: uiActions.hideFacetGroupAllFacetsModal,
  setSearchQuery: localListSearchActions.setSearchQuery,
};

export default compose(connect(mapStateToProps, dispatchProps), injectIntl)(FacetGroupAllFacets);
